/* LEGEND — chart-bottom verdict ribbon (values only).
   A thin band pinned above the time axis showing the numbers that otherwise
   live in pop-up cards: the model's strongest hold-call (LadderCard model
   view), the MV-1 forming read, and the live R/R snapshot (RrCard hero).
   No receipts, no narration, no controls — glance values only.

   The ribbon OWNS its hydrate+subscribe for both feeds (RrCard/#586 rule):
   vision + rr frames re-render exactly this strip, never the Terminal tree.
   pointer-events: none — the chart stays fully clickable through it. */
import React from 'react';
import { getRr, getVision, subscribeRr, subscribeVision } from '../data/source';
import { MODEL_FAMS } from '../lib/vision-families';
import { modelLabel } from '../lib/vision-model';
import { LANE_LABEL } from '../lib/vision-lane';
import { useVisionLane } from '../lib/use-vision-lane';
import { fmtPx4 } from '../lib/format';
import { etWhen, readState } from '../lib/vision-read';

const { useEffect: useEffectVR, useState: useStateVR } = React;
const h = React.createElement;
const F = React.Fragment;

/* Mirror of LadderCard's model-chip gates — same families (lib/vision-families,
   the MV-4 contract minus the hidden set), same thresholds, same frame.ts
   staleness rule (computedTs is bar-anchored on the wire; only frame.ts is the
   true emit time). Keep in lockstep with LadderCard.tsx. MODEL_FAMS is
   imported above. */
const MODEL_P_MIN = 0.5;
const MODEL_STALE_MS = 150_000;
const RR_STALE_MS = 90_000;

/* MV-1 class → the short code the Vision status pill already teaches. */
const shortClass = (c: string) =>
  String(c).replace('higher_low', 'HL').replace('lower_high', 'LH')
    .replace('selling_stopped', 'SS').replace('pullback', 'PB')
    .replace('impulse', 'IMP').replace('base', 'BASE');

/** MV-5's P(hold) of a target's wall / the stop's level (worker RR preview), as a compact suffix: " ·h83". */
function mv5Hold(m: any): string {
  return m && typeof m.hold === 'number' ? ' ·h' + Math.round(m.hold * 100) : '';
}

/* Strongest current hold-call off the frame's decorated levels — the same
   pick that runs the LadderCard model-view header, minus the row rounding. */
function mvTopOf(vf: any) {
  if (!vf || vf.warm === false || !Array.isArray(vf.levels)) return null;
  let top: any = null;
  for (const lv of vf.levels) {
    if (typeof lv.p !== 'number' || lv.p < MODEL_P_MIN || !MODEL_FAMS.has(lv.family)) continue;
    if (top && top.p >= lv.p) continue;
    const ind = typeof lv.type === 'string' && lv.type.includes('_')
      ? lv.type.split('_').slice(1).join('_').toUpperCase() + '·' + lv.type.split('_')[0]
      : lv.family;
    top = { p: lv.p, pct: Math.round(lv.p * 100), px: lv.price, side: lv.side, ind };
  }
  return top;
}

const K = (t: string) => h('span', { className: 'vr-k' }, t);
const SEP = () => h('span', { className: 'vr-sep' });

export function VerdictRibbon(props: any) {
  const { symbol, lastPrice } = props;
  const [vFrame, setVFrame] = useStateVR<any>(null);
  const [rrFrame, setRrFrame] = useStateVR<any>(null);

  // Hydrate + live vision feed, keyed on symbol AND lane (VisionLayer pattern,
  // incl. the seq-reset-on-respawn guard). The lane is the Vision pill's
  // (lib/vision-lane.ts); setVFrame(null) on a switch also resets the guard —
  // the lanes' publishers count seq apart.
  const lane = useVisionLane();
  useEffectVR(() => {
    let alive = true;
    setVFrame(null); // never show the previous symbol's (or lane's) read
    getVision(symbol, lane).then((r: any) => {
      // the saved read whatever its age: an old read is shown as the last read, never dropped (lib/vision-read.ts)
      if (alive && r && r.frame) {
        setVFrame((prev: any) => (prev && prev.seq >= r.frame.seq ? prev : r.frame));
      }
    }).catch(() => {});
    const off = subscribeVision(symbol, (fr: any) => {
      if (!alive || !fr) return;
      setVFrame((prev: any) => {
        if (prev && fr.seq < prev.seq && fr.ts <= prev.ts + 5000) return prev;
        return fr;
      });
    }, lane);
    return () => { alive = false; off(); };
  }, [symbol, lane]);

  // Hydrate + live RR feed (RrCard pattern).
  useEffectVR(() => {
    let alive = true;
    setRrFrame(null);
    getRr(symbol).then((r: any) => {
      if (alive && r && r.active && r.frame) {
        setRrFrame((prev: any) => (prev && prev.seq >= r.frame.seq ? prev : r.frame));
      }
    }).catch(() => {});
    const off = subscribeRr(symbol, (fr: any) => { if (alive && fr) setRrFrame(fr); });
    return () => { alive = false; off(); };
  }, [symbol]);

  // Staleness heartbeat — with both feeds quiet nothing else forces a render,
  // so age/stale could freeze without one slow tick.
  const [, force] = useStateVR(0);
  useEffectVR(() => {
    const iv = setInterval(() => force((x: number) => x + 1), 5000);
    return () => clearInterval(iv);
  }, []);

  const now = Date.now();

  // ---- MV segment values ----
  const vf = vFrame && vFrame.symbol === symbol ? vFrame : null;
  const vLive = vf && now - (vf.ts || 0) <= MODEL_STALE_MS;
  // past MODEL_STALE_MS the values stay: they are the last read, with its time (a halt or a quiet stock sends no frame)
  const vLast = !!vf && !vLive && readState(vf.ts, now) === 'last';
  const mvTop = vf ? mvTopOf(vf) : null;
  const ep1 = vf && vf.mv1 && vf.mv1.top && vf.mv1.top.class ? vf.mv1.top : null;
  const mvAge = vf ? Math.max(0, Math.floor((now - ((vf.barMs ? vf.barMs + 60_000 : vf.ts) || now)) / 1000)) : null;

  // ---- R/R segment values ----
  const rf = rrFrame && rrFrame.symbol === symbol ? rrFrame : null;
  const ep = rf && rf.state !== 'warming' ? rf.entryPreview : null;
  const rrStale = rf ? now - rf.ts > RR_STALE_MS : false;
  const isPos = rf && rf.mode === 'position' && rf.position;

  let rrSeg: any = null;
  if (isPos) {
    const P = rf.position;
    const dir = P.side === 'short' ? -1 : 1;
    const openR = P.origRisk && P.origRisk.perShare
      ? dir * (lastPrice - P.origRisk.entry) / P.origRisk.perShare
      : null;
    rrSeg = h(F, null,
      K('open'),
      h('span', { className: 'vr-v num ' + (openR != null && openR >= 0 ? 'up' : 'down') },
        openR != null ? (openR >= 0 ? '+' : '') + openR.toFixed(2) + 'R' : '—'),
      K('stop'), h('span', { className: 'vr-v num' }, '$' + fmtPx4(P.stop.price)));
  } else if (ep && ep.anchor && ep.stop) {
    const targets: any[] = ep.targets || [];
    const t1 = targets[0];
    const invFlat = lastPrice > 0 && lastPrice <= ep.stop.price;
    const liveR = t1 && !invFlat && lastPrice > ep.stop.price
      ? (t1.price - lastPrice) / (lastPrice - ep.stop.price)
      : null;
    const pass = ep.blendedRR != null ? ep.blendedRR >= ep.minRR : null;
    rrSeg = h(F, null,
      K('R/R'),
      ep.blendedRR != null ? h(F, null,
        h('span', { className: 'vr-v num ' + (pass ? 'up' : 'down') }, ep.blendedRR.toFixed(2) + 'R'),
        h('span', { className: 'vr-tag ' + (pass ? 'ok' : 'bad') }, pass ? 'PASS' : 'REJECT')) :
        h('span', { className: 'vr-v' }, '—'),
      liveR != null ? h(F, null, K('live'), h('span', { className: 'vr-v num' }, liveR.toFixed(2) + 'R'))
        : (invFlat ? h('span', { className: 'vr-tag bad' }, 'BELOW STOP') : null),
      K('E'), h('span', { className: 'vr-v num' }, '$' + fmtPx4(ep.anchor.price)),
      K('S'), h('span', { className: 'vr-v num down' }, '$' + fmtPx4(ep.stop.price) + mv5Hold(ep.stop.mv5)),
      targets.length ? h(F, null,
        K('T'),
        h('span', { className: 'vr-v num' }, targets.map((t: any) => fmtPx4(t.price) + mv5Hold(t.mv5)).join(' / '))) : null);
  }

  // Nothing worth a band — keep the chart clean.
  if (!vf && !rrSeg) return null;

  // Which model the read is from: the frame's own id (MV-5 / MV-4), else the
  // selected lane's name — so "no frame" reads as "MV-5 … no frame".
  const mvName = vf && vf.model && vf.model.version ? modelLabel(vf.model.version) : LANE_LABEL[lane];

  return h('div', { className: 'vr-ribbon' + (rrStale && !vLive ? ' stale' : '') },
    // MV verdict values
    h('span', { className: 'vr-badge' + (vLive ? '' : ' off') }, h('span', { className: 'd' }), mvName),
    mvTop ? h(F, null,
      h('span', { className: 'vr-v vr-call ' + (mvTop.side === 'support' ? 'up' : 'down') },
        mvTop.side === 'support' ? 'SUPPORT HOLDS' : 'RESISTANCE HOLDS'),
      h('span', { className: 'vr-v num ' + (mvTop.side === 'support' ? 'up' : 'down') }, mvTop.pct + '%'),
      h('span', { className: 'vr-v num' }, '$' + fmtPx4(mvTop.px)),
      h('span', { className: 'vr-v dim' }, mvTop.ind))
      : h('span', { className: 'vr-v dim' }, !vf ? 'no frame' : 'no holds'),
    ep1 ? h(F, null,
      K('forming'),
      h('span', { className: 'vr-v', style: { color: '#67e8f9' } },
        shortClass(ep1.class) + '·' + ep1.tf + ' ' + Math.round((ep1.p || 0) * 100) + '%')) : null,
    vf && typeof vf.flow === 'boolean'
      ? h('span', { className: 'vr-flow ' + (vf.flow ? 'on' : 'off'), title: vf.flow ? 'tape-informed' : 'flow-masked' }, '●')
      : null,
    mvAge != null ? h(F, null, K(vLast ? 'last read' : 'age'),
      h('span', { className: 'vr-v ' + (vLive ? '' : 'warn') }, vLast ? etWhen(vf.ts, now) : mvAge + 's')) : null,
    // R/R values
    rrSeg ? h(F, null, SEP(), rrSeg) : null);
}
